/**
 * Projects Store
 *
 * Manages project list state with TTL-based caching.
 * Provides efficient project loading with 1-minute cache to reduce API calls.
 *
 * @module store/projects/projectsStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../shared/services/logging/logger';
import { ProjectFileService } from '../../shared/middleware/services/ProjectFileService';

// Cache configuration — 5 minutes.
// Stale-while-revalidate means expired data still shows while the background
// refresh runs, so a longer TTL just reduces silent refreshes, not correctness.
const CACHE_TTL = 5 * 60_000;

/**
 * Projects store state and actions
 *
 * @typedef {Object} ProjectsState
 * @property {Array} projects - List of projects
 * @property {boolean} loading - Loading state
 * @property {string|null} error - Error message
 * @property {Object} cache - Cache data with timestamp
 * @property {Function} loadProjects - Load projects with caching
 * @property {Function} refreshProjects - Force refresh bypassing cache
 * @property {Function} clearCache - Clear projects cache
 * @property {Function} getProjectDisplayName - Get display name for project
 */

export const useProjectsStore = create((set, get) => ({
  // State
  projects: [],
  loading: false,
  error: null,
  cache: { data: null, timestamp: 0 },
  // Per-project collaborator/permission data preloaded at login
  // Shape: { [projectId]: { owner, collaborators: [], groups: [] } }
  collaboratorData: {},

  // Actions

  /**
   * Load projects with TTL-based caching
   * @param {Object} userData - User data containing accessToken
   * @param {boolean} force - Force refresh bypassing cache
   * @returns {Promise<Object>} Result object with success, data, cached flags
   */
  loadProjects: async (userData, force = false) => {
    if (!userData?.accessToken) {
      // Silent return during logout — no error state, no warning
      set({ loading: false });
      return { success: false, data: [], error: 'Session expired' };
    }

    const now = Date.now();
    const cached = get().cache;

    // CACHE HIT: data is fresh — return immediately with no state update if
    // projects are already in the store (prevents unnecessary re-renders when
    // returning to the Projects page within the TTL window).
    if (!force && cached.data && (now - cached.timestamp) < CACHE_TTL) {
      const cacheAge = Math.floor((now - cached.timestamp) / 1000);
      logger.debug('Using cached projects data', { cacheAge });
      // Only hydrate if the store was somehow emptied (e.g. between logout/login)
      if (get().projects.length === 0) {
        set({ projects: cached.data, error: null });
      }
      return { success: true, data: cached.data, cached: true };
    }

    // STALE-WHILE-REVALIDATE: if we already have projects, don't flash a
    // loading spinner — silently refresh in the background so the user keeps
    // seeing their project list while new data arrives.
    const hasExistingData = get().projects.length > 0;
    if (!hasExistingData) {
      set({ loading: true, error: null });
    }

    try {
      logger.info('Fetching fresh projects data', {
        reason: force ? 'forced' : 'cache expired'
      });

      const projectsData = await ProjectFileService.getAllProjects(userData);

      if (Array.isArray(projectsData)) {
        // Update cache
        const newCache = {
          data: projectsData,
          timestamp: now
        };

        set({
          projects: projectsData,
          cache: newCache,
          loading: false,
          error: null
        });

        logger.debug('Projects loaded successfully', {
          count: projectsData.length
        });

        return { success: true, data: projectsData, cached: false };
      } else {
        const error = 'Invalid project data format received from API';
        set({ projects: [], error, loading: false });
        logger.error('Invalid projects data format', { data: projectsData });
        return { success: false, data: [], error };
      }
    } catch (err) {
      // Suppress errors during logout (token invalidated, 401s expected)
      if (err.response?.status === 401 || !userData?.accessToken) {
        set({ loading: false });
        return { success: false, error: 'Session expired' };
      }

      const errorMessage = err.response?.data?.message ||
                          err.message ||
                          'Failed to fetch projects';

      // On silent background refresh, keep the stale projects visible rather
      // than wiping them out — user can still work with what they had.
      const keepExisting = get().projects.length > 0;
      set({ error: errorMessage, loading: false, ...(keepExisting ? {} : { projects: [] }) });

      logger.error('Failed to load projects', {
        error: errorMessage,
        stack: err.stack
      });

      return { success: false, error: errorMessage };
    }
  },

  /**
   * Force refresh projects bypassing cache
   * @param {Object} userData - User data containing accessToken
   * @returns {Promise<Object>} Result object
   */
  refreshProjects: async (userData) => {
    logger.info('Force refreshing projects (bypassing cache)');
    return get().loadProjects(userData, true);
  },

  /**
   * Optimistically add a newly created project to the store without waiting for a full refresh.
   * Updates both the live list and the cache so subsequent cache hits include the new project.
   * @param {Object} project - The project object returned by the create API
   */
  addProject: (project) => {
    set(state => {
      const newProjects = [...state.projects, project];
      return {
        projects: newProjects,
        cache: { data: newProjects, timestamp: Date.now() }
      };
    });
  },

  /**
   * Optimistically remove a deleted project from the store without waiting for a full refresh.
   * Updates both the live list and the cache so subsequent cache hits exclude the removed project.
   * @param {string} projectId - The ID of the project to remove
   */
  removeProject: (projectId) => {
    set(state => {
      const newProjects = state.projects.filter(p => p.id !== projectId);
      return {
        projects: newProjects,
        cache: { data: newProjects, timestamp: Date.now() }
      };
    });
  },

  /**
   * Optimistically update fields on a single project (e.g. status after soft-delete/archive).
   * Updates both the live list and the cache so subsequent cache hits reflect the change.
   * @param {string} projectId - The ID of the project to update
   * @param {Object} updates - Partial project fields to merge
   */
  updateProjectInStore: (projectId, updates) => {
    set(state => {
      const newProjects = state.projects.map(p =>
        p.id === projectId ? { ...p, ...updates } : p
      );
      return {
        projects: newProjects,
        cache: { data: newProjects, timestamp: Date.now() }
      };
    });
  },

  /**
   * Clear projects cache and state
   */
  clearCache: () => {
    logger.debug('Clearing projects cache');
    set({
      projects: [],
      error: null,
      cache: { data: null, timestamp: 0 },
      collaboratorData: {},
      permissionsPreloaded: false,
      permissionsPreloading: false,
    });
  },

  /**
   * Store collaborator/permission data for a single project.
   * Called by the preloader and by ProjectView after a live fetch.
   * @param {string} projectId - Project UUID
   * @param {Object} data - { owner, collaborators, groups }
   */
  setCollaboratorData: (projectId, data) => {
    set(state => ({
      collaboratorData: { ...state.collaboratorData, [projectId]: data }
    }));
  },

  /**
   * Flag set by OrganizationPreloader when all permissions have been fetched.
   * ProjectView checks this to skip its own permission fetch and show projects instantly.
   */
  permissionsPreloaded: false,
  setPermissionsPreloaded: (value) => set({ permissionsPreloaded: value }),

  permissionsPreloading: false,
  setPermissionsPreloading: (value) => set({ permissionsPreloading: value }),

  /**
   * True while OrganizationPreloader is mid-flight fetching permissions.
   * ProjectView waits for this to be false before deciding to fetch anything itself,
   * preventing the double-fire that causes 429 rate-limit bursts.
   */
  permissionsPreloading: false,
  setPermissionsPreloading: (value) => set({ permissionsPreloading: value }),

  /**
   * Get display name for a project
   * @param {string} projectId - Project ID
   * @returns {string} Display name or project ID
   */
  getProjectDisplayName: (projectId) => {
    const { projects } = get();

    if (!Array.isArray(projects)) return projectId;

    const project = projects.find(p => {
      if (typeof p === 'string') {
        return p === projectId;
      } else if (p && typeof p === 'object') {
        return p.id === projectId;
      }
      return false;
    });

    if (project && typeof project === 'object') {
      return project.displayName || project.name || project.id || projectId;
    }

    return projectId;
  }
}));

// Computed selectors

/**
 * Normalize project to ID string
 * @param {*} project - Project object or string
 * @returns {string} Normalized project ID
 */
const normalizeProjectId = (project) => {
  let projectId;

  if (typeof project === 'string') {
    projectId = project;
  } else if (project && typeof project === 'object') {
    projectId = project.id ||
               project.name ||
               project.projectName ||
               project.title ||
               project.displayName ||
               '';
  } else {
    projectId = String(project || '');
  }

  // Remove trailing slashes and return
  return String(projectId || '').replace(/\/$/, '');
};

/**
 * Get unique project IDs (normalized and sorted)
 * @returns {Array<string>} Array of unique project IDs
 */
export const useUniqueProjects = () =>
  useProjectsStore(
    useShallow(state => {
      const { projects } = state;

      if (!Array.isArray(projects) || projects.length === 0) {
        return [];
      }

      const projectIds = projects
        .filter(p => {
          // Only include ACTIVE projects — hide archived (INACTIVE) and deleted
          if (typeof p !== 'object' || !p) return true; // string IDs pass through
          const status = p.status ? p.status.toUpperCase() : 'ACTIVE';
          return status === 'ACTIVE';
        })
        .map(normalizeProjectId)
        .filter(id => id && id.trim() !== '')
        .sort();

      return projectIds;
    })
  );

// Granular selector hooks

/**
 * Get projects list
 * @returns {Array} Projects array
 */
export const useProjects = () =>
  useProjectsStore(state => state.projects);

/**
 * Get loading state
 * @returns {boolean} Loading status
 */
export const useProjectsLoading = () =>
  useProjectsStore(state => state.loading);

/**
 * Get error state
 * @returns {string|null} Error message
 */
export const useProjectsError = () =>
  useProjectsStore(state => state.error);

/**
 * Get cache info
 * @returns {Object} Cache data with timestamp
 */
export const useProjectsCache = () =>
  useProjectsStore(state => state.cache);

/**
 * Get all projects actions
 * @returns {Object} Projects actions
 */
export const useProjectsActions = () =>
  useProjectsStore(
    useShallow(state => ({
      loadProjects: state.loadProjects,
      refreshProjects: state.refreshProjects,
      clearCache: state.clearCache,
      getProjectDisplayName: state.getProjectDisplayName,
      setCollaboratorData: state.setCollaboratorData,
      addProject: state.addProject,
      removeProject: state.removeProject,
      updateProjectInStore: state.updateProjectInStore
    }))
  );

/**
 * Get the full collaboratorData map (all projects)
 * @returns {Object} { [projectId]: { owner, collaborators, groups } }
 */
export const useCollaboratorData = () =>
  useProjectsStore(useShallow(state => state.collaboratorData));
